import React, { useState, useEffect, useMemo, useRef } from "react"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"

import {
  Container,
  InputWrapper,
  SearchInput,
  ClearButton,
  SuggestionList,
  SuggestionItem,
} from "./index.style"
import { filterSuggestions } from "./utils"

function SearchBar({
  data,
  filterFields,
  value,
  placeholder,
  onSelectFilterValue,
  onRemoveSelectedValue,
  timingBeforeSelectActivation,
  matchMode,
  caseSensitive,
  showSuggestions,
  defaultColor,
  sideMenuColor,
  activeBorder,
  dropdownBorderRadius,
  dropdownBorderColor,
  dropdownBorderSize,
  dropdownBorderStyle,
}) {
  const isMultiField = Array.isArray(filterFields) && filterFields.length > 1

  const [inputValue, setInputValue] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null)
  const [dropdownRect, setDropdownRect] = useState(null)
  const inputRef = useRef(null)
  const containerRef = useRef(null)

  // Sync controlled value from parent (e.g. when filter is reset externally)
  useEffect(() => {
    if (!value || value === "default") {
      setInputValue("")
    } else if (typeof value === "string") {
      setInputValue(value)
    }
  }, [value])

  // Update dropdown position when it opens or input changes
  useEffect(() => {
    if (showDropdown && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
  }, [showDropdown, inputValue])

  // Suggestions computed from current input against data.unique (merged across all fields)
  const suggestions = useMemo(() => {
    if (!showSuggestions || !inputValue || !data?.unique) return []
    return filterSuggestions(data.unique, inputValue, matchMode, caseSensitive).slice(0, 10)
  }, [inputValue, data, matchMode, caseSensitive, showSuggestions])

  const sendFilter = text => {
    if (!text) {
      onRemoveSelectedValue()
      return
    }

    if (isMultiField) {
      // Build per-field match maps and dispatch a single multi-field filter
      const byField = {}
      filterFields.forEach(fieldId => {
        const fieldValues = data?.byField?.[fieldId] || []
        const matches = filterSuggestions(fieldValues, text, matchMode, caseSensitive)
        if (matches.length > 0) byField[fieldId] = matches
      })
      if (Object.keys(byField).length > 0) {
        onSelectFilterValue({ byField, operation: "==" })
      } else {
        onRemoveSelectedValue()
      }
      return
    }

    // Single-field: filter data.unique locally and send exact matches
    const matches = filterSuggestions(data?.unique || [], text, matchMode, caseSensitive)
    if (matches.length) {
      onSelectFilterValue({ value: matches, operation: "==" })
    } else {
      // No match found: clear any active filter
      onRemoveSelectedValue()
    }
  }

  const handleInputChange = e => {
    const newValue = e.target.value
    setInputValue(newValue)
    setShowDropdown(true)

    if (debounceTimer) clearTimeout(debounceTimer)

    if (!newValue) {
      onRemoveSelectedValue()
      return
    }

    if (timingBeforeSelectActivation === 0) {
      sendFilter(newValue)
    } else {
      const timer = setTimeout(() => sendFilter(newValue), timingBeforeSelectActivation)
      setDebounceTimer(timer)
    }
  }

  const handleClear = () => {
    if (debounceTimer) clearTimeout(debounceTimer)
    setInputValue("")
    setShowDropdown(false)
    onRemoveSelectedValue()
    inputRef.current?.focus()
  }

  const handleSuggestionClick = suggestion => {
    setInputValue(suggestion)
    setShowDropdown(false)

    if (isMultiField) {
      const byField = {}
      filterFields.forEach(fieldId => {
        const fieldValues = data?.byField?.[fieldId] || []
        if (fieldValues.includes(suggestion)) byField[fieldId] = [suggestion]
      })
      if (Object.keys(byField).length > 0) {
        onSelectFilterValue({ byField, operation: "==" })
        return
      }
    }

    onSelectFilterValue({ value: [suggestion], operation: "==" })
  }

  const dropdown =
    showDropdown && showSuggestions && suggestions.length > 0 && dropdownRect
      ? ReactDOM.createPortal(
          <SuggestionList
            scrollBarColor={sideMenuColor}
            style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
          >
            {suggestions.map((s, i) => (
              <SuggestionItem key={i} onMouseDown={() => handleSuggestionClick(s)}>
                {s}
              </SuggestionItem>
            ))}
          </SuggestionList>,
          document.body
        )
      : null

  return (
    <Container
      ref={containerRef}
      dropdownBorderRadius={dropdownBorderRadius}
      dropdownBorderColor={dropdownBorderColor}
      dropdownBorderSize={dropdownBorderSize}
      dropdownBorderStyle={dropdownBorderStyle}
      activeBorder={activeBorder}
    >
      <InputWrapper defaultColor={defaultColor}>
        <i className="fas fa-search" />
        <SearchInput
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        />
        {inputValue && (
          <ClearButton onClick={handleClear}>
            <i className="fas fa-times" />
          </ClearButton>
        )}
      </InputWrapper>
      {dropdown}
    </Container>
  )
}

SearchBar.defaultProps = {
  data: { unique: [], byField: {} },
  filterFields: null,
  value: "default",
  placeholder: "Rechercher...",
  timingBeforeSelectActivation: 500,
  matchMode: "contains",
  caseSensitive: false,
  showSuggestions: true,
  defaultColor: "#5588FF",
  sideMenuColor: "#ccc",
  activeBorder: false,
  dropdownBorderRadius: "5px",
  dropdownBorderColor: "#000",
  dropdownBorderSize: "1px",
  dropdownBorderStyle: "solid",
  onSelectFilterValue: obj => console.log(obj),
  onRemoveSelectedValue: () => {},
}

SearchBar.propTypes = {
  data: PropTypes.object,
  filterFields: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  placeholder: PropTypes.string,
  timingBeforeSelectActivation: PropTypes.number,
  matchMode: PropTypes.oneOf(["contains", "startsWith", "exact"]),
  caseSensitive: PropTypes.bool,
  showSuggestions: PropTypes.bool,
  defaultColor: PropTypes.string,
  sideMenuColor: PropTypes.string,
  activeBorder: PropTypes.bool,
  dropdownBorderRadius: PropTypes.string,
  dropdownBorderColor: PropTypes.string,
  dropdownBorderSize: PropTypes.string,
  dropdownBorderStyle: PropTypes.string,
  onSelectFilterValue: PropTypes.func,
  onRemoveSelectedValue: PropTypes.func,
}

export default SearchBar
